import { Container, Graphics, Rectangle, Text, TextStyle } from "pixi.js";
import { CardBackgroundPattern } from "./CardBackgroundPattern";

export type Suit = "♠" | "♥" | "♦" | "♣";
export type Rank = "A" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "J" | "Q" | "K";

export interface CardData {
  suit: Suit;
  rank: Rank;
  faceUp: boolean;
}

export class Card extends Container {
  public static readonly WIDTH = 80;
  public static readonly HEIGHT = 120;

  public readonly suit: Suit;
  public readonly rank: Rank;
  
  private _faceUp: boolean;
  private cardGraphic: Graphics;
  private valueText: Text;
  private suitText: Text;
  private cardMask: Graphics | null = null;
  private cardShadow: Graphics | null = null;
  private lastRenderedFaceUp: boolean | null = null; // Cache render state

  constructor(data: CardData) {
    super();
    this.suit = data.suit;
    this.rank = data.rank;
    this._faceUp = data.faceUp;

    // Set contextual name for debugging
    this.name = `Card-${this.rank}${this.suit}`;

    this.hitArea = new Rectangle(0, 0, Card.WIDTH, Card.HEIGHT);
    this.eventMode = "static";
    this.cursor = "pointer";

    this.cardGraphic = new Graphics();
    this.cardGraphic.name = "CardGraphic";
    this.valueText = this.createValueText();
    this.valueText.name = "ValueText";
    this.suitText = this.createSuitText();
    this.suitText.name = "SuitText";

    this.addChild(this.cardGraphic);
    this.addChild(this.valueText);
    this.addChild(this.suitText);

    // Initialize shadow and mask once
    this.initializeShadowAndMask();

    this.render();
  }

  private createValueText(): Text {
    return new Text({
      text: "",
      style: new TextStyle({
        fontFamily: "Arial",
        fontSize: 24,
        fill: 0x000000,
        fontWeight: "bold",
      }),
    });
  }

  private createSuitText(): Text {
    const text = new Text({
      text: "",
      style: new TextStyle({
        fontFamily: "Arial",
        fontSize: 32,
        fill: 0x000000,
      }),
    });
    text.anchor.set(0.5, 0.5);
    return text;
  }

  get faceUp(): boolean {
    return this._faceUp;
  }

  set faceUp(value: boolean) {
    if (this._faceUp !== value) {
      this._faceUp = value;
      this.render();
    }
  }

  /**
   * Initialize shadow and mask once - reuse them instead of recreating
   */
  private initializeShadowAndMask(): void {
    if (!this.cardShadow) {
      this.cardShadow = CardBackgroundPattern.createCardShadow(Card.WIDTH, Card.HEIGHT);
      this.addChildAt(this.cardShadow, 0); // Add at index 0 (behind everything)
    }
    
    if (!this.cardMask) {
      this.cardMask = CardBackgroundPattern.createCardMask(Card.WIDTH, Card.HEIGHT);
      this.cardGraphic.mask = this.cardMask;
      this.addChild(this.cardMask);
    }
  }

  private render(): void {
    // Performance optimization: Only re-render if faceUp state actually changed
    if (this.lastRenderedFaceUp === this._faceUp) {
      return; // Skip render if state hasn't changed
    }

    // Initialize shadow and mask once (reuse them)
    this.initializeShadowAndMask();

    // Clear and redraw card graphic only when needed
    this.cardGraphic.clear();

    if (this._faceUp) {
      this.renderFaceUp();
    } else {
      this.renderFaceDown();
    }

    // Cache the rendered state
    this.lastRenderedFaceUp = this._faceUp;
  }

  private renderFaceUp(): void {
    // Draw pattern using CardBackgroundPattern utility (low coupling)
    CardBackgroundPattern.drawFaceUpPattern(this.cardGraphic, Card.WIDTH, Card.HEIGHT);
    
    // Mask is already initialized and attached, no need to recreate

    const color = this.getColor() === "red" ? 0xff0000 : 0x000000;

    // Only update text if it changed (performance optimization)
    if (this.valueText.text !== this.rank) {
      this.valueText.text = this.rank;
    }
    if (this.valueText.style.fill !== color) {
      this.valueText.style.fill = color;
    }
    // Cache positions - only set if not already set
    if (this.valueText.x !== 10 || this.valueText.y !== 10) {
      this.valueText.x = 10;
      this.valueText.y = 10;
    }
    if (!this.valueText.visible) {
      this.valueText.visible = true;
    }

    if (this.suitText.text !== this.suit) {
      this.suitText.text = this.suit;
    }
    if (this.suitText.style.fill !== color) {
      this.suitText.style.fill = color;
    }
    const suitX = Card.WIDTH / 2;
    const suitY = Card.HEIGHT / 2;
    if (this.suitText.x !== suitX || this.suitText.y !== suitY) {
      this.suitText.x = suitX;
      this.suitText.y = suitY;
    }
    if (!this.suitText.visible) {
      this.suitText.visible = true;
    }
  }

  private renderFaceDown(): void {
    // Draw pattern using CardBackgroundPattern utility (low coupling)
    CardBackgroundPattern.drawFaceDownPattern(this.cardGraphic, Card.WIDTH, Card.HEIGHT);
    
    // Mask is already initialized and attached, no need to recreate

    // Only update visibility if needed (performance optimization)
    if (this.valueText.visible) {
      this.valueText.visible = false;
    }
    if (this.suitText.visible) {
      this.suitText.visible = false;
    }
  }

  flip(): void {
    this.faceUp = !this._faceUp;
  }

  getNumericValue(): number {
    const values: Record<string, number> = {
      A: 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7,
      "8": 8, "9": 9, "10": 10, J: 11, Q: 12, K: 13,
    };
    return values[this.rank];
  }

  getColor(): "red" | "black" {
    return this.suit === "♥" || this.suit === "♦" ? "red" : "black";
  }

  isOppositeColor(other: Card): boolean {
    return this.getColor() !== other.getColor();
  }
}
